import { useState } from 'react';
import { Button, Modal, OverlayTrigger, Stack, Tooltip } from 'react-bootstrap';
import { I18nextProvider } from 'react-i18next';

import { faCheckCircle, faSave } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18nReport from 'src/translation/i18nReport';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { EyesPosition } from 'src/models/Session.model';

import useActionAnalyzeModel from 'src/hooks/useActionAnalyzeModel';
import { useHandleViewReport } from 'src/hooks/useHandleViewReport';
import useUiTranslation from 'src/hooks/useUiTranslation';

import ReportViewer from '../ReportViewer';
import RetakeActionButtons from './RetakeActionButtons';

interface ProgressModalFooterProps {
  handleRetake: (position: EyesPosition) => void;
  hasCompletedExam: boolean;
  shouldShowDilationRecommendation: boolean;
  shouldShowReachMaxRecommendedAttempts: boolean;
  handleCloseAnalyzingProgressModal: () => void;
  handleResetSession: () => void;
}

const ProgressModalFooter = ({
  handleRetake,
  hasCompletedExam,
  shouldShowDilationRecommendation,
  shouldShowReachMaxRecommendedAttempts,
  handleCloseAnalyzingProgressModal,
  handleResetSession,
}: ProgressModalFooterProps) => {
  const { tPages, tCommon } = useUiTranslation();
  const {
    userSettings: { userPermission },
    session,
  } = useRemoteBoundStore();

  const {
    handleCompleteExam,
    handleSaveAndDilate,
    handleEndSession,
    isClosingSession,
  } = useActionAnalyzeModel(
    handleCloseAnalyzingProgressModal,
    handleResetSession,
    session,
  );
  const [showViewer, setShowViewer] = useState<boolean>(false);

  const handleSetShowViewer = (showViewer: boolean) => {
    setShowViewer(showViewer);
  };

  const { handleViewReport } = useHandleViewReport();

  const onViewReport = async () => {
    await handleViewReport(session);
    handleSetShowViewer(true);
  };

  return (
    <Modal.Footer>
      {isClosingSession ? (
        session.graded && (
          <Button variant="primary" disabled>
            <FontAwesomeIcon icon={faSave} className="me-2" />
            {tPages('newExam.saving')}
          </Button>
        )
      ) : (
        <Stack direction="horizontal" gap={4}>
          {session.graded ? (
            <>
              {hasCompletedExam &&
                (userPermission.read ? (
                  <>
                    <Button variant="primary-100" onClick={onViewReport}>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      {tCommon('report.title')}
                    </Button>
                    {showViewer && (
                      <I18nextProvider i18n={i18nReport}>
                        <ReportViewer
                          show={showViewer}
                          handleCloseReportViewer={() => {
                            handleCompleteExam();
                            handleSetShowViewer(false);
                          }}
                          session={session}
                        />
                      </I18nextProvider>
                    )}
                  </>
                ) : (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {tCommon('message.error.noPermissionViewReport')}
                      </Tooltip>
                    }
                  >
                    <Button
                      variant="primary-100"
                      onClick={handleSaveAndDilate}
                      disabled={!userPermission.read}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      {tCommon('report.title')}
                    </Button>
                  </OverlayTrigger>
                ))}

              {(shouldShowReachMaxRecommendedAttempts || hasCompletedExam) && (
                <Button
                  variant="primary"
                  className="shadow"
                  onClick={handleCompleteExam}
                >
                  {tPages('newExam.completeExam')}
                </Button>
              )}

              {shouldShowDilationRecommendation && !hasCompletedExam && (
                <Button variant="primary" onClick={handleSaveAndDilate}>
                  {tPages('newExam.saveAndDilate')}
                </Button>
              )}

              {session.graded && (
                <RetakeActionButtons
                  handleRetake={handleRetake}
                  handleCloseAnalyzingProgressModal={
                    handleCloseAnalyzingProgressModal
                  }
                />
              )}

              {!hasCompletedExam && !shouldShowReachMaxRecommendedAttempts && (
                <Button
                  variant="warning"
                  disabled={!session.graded}
                  onClick={handleEndSession}
                >
                  {tPages('newExam.endSession')}
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="warning"
              disabled={!session.graded}
              onClick={handleEndSession}
            >
              {tPages('newExam.endSession')}
            </Button>
          )}
        </Stack>
      )}
    </Modal.Footer>
  );
};

export default ProgressModalFooter;
